import { Badge, Box, Button, HStack, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiPlus } from 'react-icons/fi'
import { useIsMobile } from '../../../hooks/useIsMobile'
import type { ContactSearchResult } from '../../../types/contact'

interface Props {
  contact: ContactSearchResult
  isAttendee: boolean
  isDuplicate: boolean
  onAdd: (contactId: string) => void
  onViewDetails: (contactId: string) => void
  adding: boolean
}

function DistrictBadges({ c }: { c: ContactSearchResult }) {
  return (
    <>
      {c.stateHouseDistrict && <Badge>HD-{c.stateHouseDistrict}</Badge>}
      {c.stateSenateDistrict && <Badge>SD-{c.stateSenateDistrict}</Badge>}
      {c.congressionalDistrict && <Badge>CD-{c.congressionalDistrict}</Badge>}
    </>
  )
}

export function RecruitResultRow({ contact: c, isAttendee, isDuplicate, onAdd, onViewDetails, adding }: Props) {
  const isMobile = useIsMobile()

  // Clickable name → full contact-detail modal, same affordance as the Roster tab,
  // so an organizer can verify the person before adding them.
  const nameLink = (
    <Text
      fontWeight="medium"
      color="accent"
      cursor="pointer"
      _hover={{ textDecoration: 'underline' }}
      onClick={() => onViewDetails(c.id)}
    >
      {c.fullName}
    </Text>
  )
  const prior = c.priorLobbyDayCount ?? 0
  const priorBadge = prior > 0 && (
    <Badge colorPalette="green">Attended {prior} prior {prior === 1 ? 'lobby day' : 'lobby days'}</Badge>
  )
  const dupHint = isDuplicate && (
    <Tooltip content="Multiple contacts share this name — check district/employer">
      <Badge colorPalette="orange">possible duplicate</Badge>
    </Tooltip>
  )

  const addButton = isAttendee ? (
    <Button colorPalette="green" variant="outline" disabled minH={isMobile ? '44px' : undefined} flexShrink={0}>
      Added
    </Button>
  ) : (
    <Button
      colorPalette="brand"
      loading={adding}
      onClick={() => onAdd(c.id)}
      minH={isMobile ? '44px' : undefined}
      flexShrink={0}><FiPlus />Add
          </Button>
  )

  const borderProps = prior > 0
    ? { borderLeft: '3px solid', borderLeftColor: 'info.fg' }
    : {}

  const meta = [c.city, c.state].filter(Boolean).join(', ') + (c.employerName ? ` · ${c.employerName}` : '')

  const badges = (
    <Wrap gap={1}>
      <DistrictBadges c={c} />
      {(c.unionName || c.unionMemberNumber) && (
        <WrapItem>
          <Badge colorPalette="purple">
            {[c.unionName, c.unionMemberNumber && `#${c.unionMemberNumber}`].filter(Boolean).join(' · ')}
          </Badge>
        </WrapItem>
      )}
      {priorBadge && <WrapItem>{priorBadge}</WrapItem>}
      {dupHint && <WrapItem>{dupHint}</WrapItem>}
    </Wrap>
  )

  if (isMobile) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={3} bg="surface.2" {...borderProps}>
        <VStack align="stretch" gap={2}>
          <VStack align="start" gap={1}>
            <HStack>
              <Text fontWeight="medium">{c.fullName}</Text>
              {c.age != null && <Text color="gray.500" fontSize="sm">· {c.age}</Text>}
            </HStack>
            {meta.trim() && <Text fontSize="xs" color="gray.600">{meta}</Text>}
          </VStack>
          {badges}
          {addButton}
        </VStack>
      </Box>
    );
  }

  return (
    <HStack borderWidth="1px" borderRadius="md" p={3} bg="surface.2" justify="space-between" align="center" gap={4} {...borderProps}>
      <VStack align="start" gap={1} flex="1" minW={0}>
        <HStack gap={2}>
          {nameLink}
          {c.age != null && <Text color="gray.500" fontSize="sm">· {c.age}</Text>}
        </HStack>
        {meta.trim() && <Text fontSize="xs" color="gray.500">{meta}</Text>}
        {badges}
      </VStack>
      {addButton}
    </HStack>
  );
}
